import './index.css'
import { Theme } from '@radix-ui/themes'
import cn from 'clsx'

import React, { useCallback, useContext, useMemo } from 'react'
import type { ReactModal$Props } from 'react-modal'
import Modal from 'react-modal'
import { useSelector } from 'hooks/use-redux'
import { useMediaQuery } from 'react-responsive'
import type { State } from 'types/redux'
import { ThemeContext, ThemeProvider } from 'contexts/theme'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import ErrorBoundary from 'components/common/error-boundary'
import Button from 'components/ui/button'
import { HoverContext } from 'components/ui/hover-provider'
import type { ModalStyle } from 'components/utils/modalStyles'
import modalStyles, {
  darkThemeModalStyles,
  mobileModalAdditionalStyles,
} from 'components/utils/modalStyles'
type Props = ReactModal$Props & {
  errorHandler?: (details: { error: Error; isConnectError: boolean }) => unknown
  isOpen?: boolean
  modalType?: 'alert' | 'default'
  renderError?: (details: {
    error: Error
    isConnectError: boolean
  }) => React.ReactNode
  showCloseButton?: boolean
  style?: Partial<ModalStyle>
  onRequestClose?: ReactModal$Props['onRequestClose']
}

/**
 * A customizable modal component with theme support, error handling, and responsive design
 * @param errorHandler - Optional callback function to handle errors that occur within the modal
 * @param isOpen - Whether the modal is open - defaults to true
 * @param modalType - Type of modal styling ('alert' or 'default') - defaults to 'default'
 * @param renderError - Function to render custom error UI, receives error details - defaults to empty div
 * @param showCloseButton - Whether to show the close button in the top right - defaults to false
 * @param style - Custom modal styles object - defaults to modalStyles
 * @param onRequestClose - Callback function called when modal should be closed
 * @param restProps - Additional props passed to the underlying Modal component
 * @returns A Modal component wrapped in error boundary with theme and responsive support
 */
const DiscoModal = ({
  errorHandler,
  isOpen = true,
  modalType = 'default',
  renderError = () => <div />,
  showCloseButton,
  style = modalStyles,
  onRequestClose,
  ...restProps
}: Props): React.ReactNode => {
  const desktop = useMediaQuery({
    minWidth: 601,
  })
  const isPhone = useSelector(isDeviceTypePhone)
  const trackId: number | null | undefined = useSelector(
    (state: State) => state.media?.trackOriginalId,
  )
  const { setHover } = useContext(HoverContext)
  const theme = useContext(ThemeContext).theme
  const closeModal = useCallback(
    (
      event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => {
      if (setHover) {
        setHover(false)
      }

      if (onRequestClose) {
        onRequestClose(event)
      }
    },
    [onRequestClose, setHover],
  )
  const defaultErrorHandler = useCallback(() => {
    closeModal()
  }, [closeModal])

  const renderCloseIcon = (children: React.ReactNode) => (
    <div
      className={cn('DiscoModal', {
        'DiscoModal--Alert': modalType === 'alert',
      })}
    >
      <div className="DiscoModal_Close">
        <Button appearance="icon" icon="close" onClick={closeModal} />
      </div>
      {children}
    </div>
  )

  const modalProps: ReactModal$Props = useMemo(() => {
    let paddingBottom: string | number =
      'calc(var(--player-height) + var(--player-inset-bottom) + 24px)'

    if (Boolean(theme)) {
      // TODO: Move ThemeProvider to top level
      // var(--player-height) + var(--player-inset-bottom) + offset
      paddingBottom = 72 + 62 + 24
    }

    let modalStyle = {
      overlay: {
        ...style?.overlay,
        paddingBottom:
          trackId && desktop ? paddingBottom : style?.overlay?.padding,
      },
      content: style?.content ?? {},
    }
    let children = restProps.children

    if (showCloseButton) {
      children = renderCloseIcon(children)
    }

    if (theme === 'dark') {
      if (style) {
        modalStyle.overlay = {
          ...style.overlay,
        }
        modalStyle.content = {
          ...style.content,
        }
      } else {
        modalStyle.overlay = {
          ...modalStyle.overlay,
          ...darkThemeModalStyles.overlay,
        }
        modalStyle.content = {
          ...modalStyle.content,
          ...darkThemeModalStyles.content,
        }
      }
    }

    if (isPhone) {
      modalStyle.overlay = {
        ...modalStyle.overlay,
        ...mobileModalAdditionalStyles.overlay,
      }
      modalStyle.content = {
        ...modalStyle.content,
        ...mobileModalAdditionalStyles.content,
      }
    }

    return {
      ...restProps,
      overlayElement: (props, children) => (
        <Theme hasBackground>
          <ThemeProvider
            value={{
              theme,
            }}
          >
            <div {...props}>{children}</div>
          </ThemeProvider>
        </Theme>
      ),
      children,
      isOpen,
      style: modalStyle,
      onRequestClose: closeModal,
    }
  }, [closeModal, isOpen, renderCloseIcon, restProps, style, trackId, desktop])
  return (
    <ErrorBoundary
      renderError={renderError}
      errorHandler={errorHandler || defaultErrorHandler}
    >
      <Modal
        {...modalProps}
        onRequestClose={evt => {
          if (evt?.nativeEvent) {
            // don't bubble ESC
            evt.nativeEvent.stopImmediatePropagation()
          }

          if (modalProps.onRequestClose) {
            modalProps.onRequestClose(evt)
          }
        }}
      />
    </ErrorBoundary>
  )
}

export default DiscoModal
